import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useDocOrganization } from '../../../../../hooks/useDocOrganization';
import { getChildFolders, isAncestorFolder } from '../../../../../utils/docOrganization';
import { DOC_DRAG_TYPE, FOLDER_DRAG_TYPE, UNFILED_EXPANDED_KEY } from '../constants';

const readUnfiledExpanded = () => {
  try {
    return localStorage.getItem(UNFILED_EXPANDED_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * State and handlers for the sidebar Docs area:
 * folder tree + Unfiled inbox, expand/create, and drag-and-drop moves.
 */
const useDocsSection = ({ docs = [], selectedDocId = null }) => {
  const organization = useDocOrganization();
  const {
    ready,
    folders,
    getDocFolderId,
    createFolder,
    moveDocToFolder,
    moveFolder,
    moveFolderNextTo,
  } = organization;

  const [unfiledExpanded, setUnfiledExpanded] = useState(readUnfiledExpanded);
  const [collapsedFolders, setCollapsedFolders] = useState({});
  const [creatingFolder, setCreatingFolder] = useState(false);
  /** null = create at root; string = create under that parent */
  const [creatingUnderParentId, setCreatingUnderParentId] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [dropTargetId, setDropTargetId] = useState(null);
  const [dropHint, setDropHint] = useState(null);
  const [draggedFolderId, setDraggedFolderId] = useState(null);
  const newFolderInputRef = useRef(null);

  useEffect(() => {
    if (!creatingFolder) return;
    const input = newFolderInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [creatingFolder]);

  const sortedDocs = useMemo(() => {
    return [...docs].sort((a, b) => {
      const at = a.updatedAt || a.createdAt || '';
      const bt = b.updatedAt || b.createdAt || '';
      return bt.localeCompare(at);
    });
  }, [docs]);

  const docsByFolder = useMemo(() => {
    const map = new Map(folders.map((f) => [f.id, []]));
    for (const doc of sortedDocs) {
      const folderId = getDocFolderId(doc.id);
      if (folderId && map.has(folderId)) map.get(folderId).push(doc);
    }
    return map;
  }, [sortedDocs, folders, getDocFolderId]);

  const unfiledDocs = useMemo(
    () => sortedDocs.filter((doc) => !getDocFolderId(doc.id)),
    [sortedDocs, getDocFolderId]
  );

  useEffect(() => {
    try {
      localStorage.setItem(UNFILED_EXPANDED_KEY, unfiledExpanded ? '1' : '0');
    } catch {
      /* ignore quota / private mode */
    }
  }, [unfiledExpanded]);

  // Reveal the inbox when the user opens an unfiled doc (not on every org tick).
  useEffect(() => {
    if (!selectedDocId) return;
    if (getDocFolderId(selectedDocId)) return;
    setUnfiledExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocId]);

  const rootFolders = useMemo(
    () => getChildFolders(folders, null),
    [folders]
  );

  const handleToggleFolderExpand = useCallback((folderId) => {
    setCollapsedFolders((prev) => ({ ...prev, [folderId]: !prev[folderId] }));
  }, []);

  const canCollapseAll = unfiledExpanded || folders.some((f) => !collapsedFolders[f.id]);

  const handleCollapseAll = useCallback(() => {
    setCollapsedFolders(Object.fromEntries(folders.map((f) => [f.id, true])));
    setUnfiledExpanded(false);
  }, [folders]);

  const handleStartCreateFolder = useCallback((e, parentId = null) => {
    e?.stopPropagation?.();
    e?.currentTarget?.blur?.();
    if (parentId) {
      setCollapsedFolders((prev) => ({ ...prev, [parentId]: false }));
    }
    setCreatingUnderParentId(parentId);
    setNewFolderName('');
    setCreatingFolder(true);
  }, []);

  const handleCommitNewFolder = useCallback(() => {
    const name = newFolderName.trim();
    const parentId = creatingUnderParentId;
    setCreatingFolder(false);
    setCreatingUnderParentId(null);
    setNewFolderName('');
    if (name) createFolder(name, parentId);
  }, [newFolderName, createFolder, creatingUnderParentId]);

  const handleCancelNewFolder = useCallback(() => {
    setCreatingFolder(false);
    setCreatingUnderParentId(null);
    setNewFolderName('');
  }, []);

  const handleNewFolderKeyDown = useCallback(
    (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCommitNewFolder();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancelNewFolder();
      }
    },
    [handleCommitNewFolder, handleCancelNewFolder]
  );

  const handleDragOverTarget = useCallback((e, targetId, zone = 'middle') => {
    const types = e.dataTransfer.types;
    if (!types.includes(DOC_DRAG_TYPE) && !types.includes(FOLDER_DRAG_TYPE)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (targetId === 'unfiled') {
      setDropTargetId('unfiled');
      setDropHint(null);
      return;
    }

    setDropTargetId(null);
    if (draggedFolderId && (targetId === draggedFolderId
      || isAncestorFolder(folders, targetId, draggedFolderId))) {
      setDropHint(null);
      return;
    }
    setDropHint((prev) =>
      prev?.folderId === targetId && prev?.zone === zone
        ? prev
        : { folderId: targetId, zone }
    );
  }, [draggedFolderId, folders]);

  const handleDragLeaveTarget = useCallback(() => {
    setDropTargetId(null);
    setDropHint(null);
  }, []);

  const handleFolderDragStart = useCallback((folderId) => {
    setDraggedFolderId(folderId);
  }, []);

  const handleFolderDragEnd = useCallback(() => {
    setDraggedFolderId(null);
    setDropTargetId(null);
    setDropHint(null);
  }, []);

  const handleDropOnFolder = useCallback(
    (e, folderId, zone = 'middle') => {
      e.preventDefault();
      e.stopPropagation();
      setDropTargetId(null);
      setDropHint(null);
      setDraggedFolderId(null);
      const docId = e.dataTransfer.getData(DOC_DRAG_TYPE);
      const draggedFolder = e.dataTransfer.getData(FOLDER_DRAG_TYPE);
      if (docId) {
        moveDocToFolder(docId, folderId);
        return;
      }
      if (!draggedFolder || draggedFolder === folderId) return;
      if (isAncestorFolder(folders, folderId, draggedFolder)) return;
      if (zone === 'above') moveFolderNextTo(draggedFolder, folderId, 'before');
      else if (zone === 'below') moveFolderNextTo(draggedFolder, folderId, 'after');
      else moveFolder(draggedFolder, folderId);
    },
    [moveDocToFolder, moveFolder, moveFolderNextTo, folders]
  );

  const handleDropOnUnfiled = useCallback(
    (e) => {
      e.preventDefault();
      setDropTargetId(null);
      setDropHint(null);
      setDraggedFolderId(null);
      const docId = e.dataTransfer.getData(DOC_DRAG_TYPE);
      const draggedFolder = e.dataTransfer.getData(FOLDER_DRAG_TYPE);
      if (docId) moveDocToFolder(docId, null);
      else if (draggedFolder) moveFolder(draggedFolder, null);
    },
    [moveDocToFolder, moveFolder]
  );

  return {
    organization,
    ready,
    folders,
    rootFolders,
    unfiledDocs,
    docsByFolder,
    unfiledExpanded,
    setUnfiledExpanded,
    collapsedFolders,
    creatingFolder,
    creatingUnderParentId,
    newFolderName,
    setNewFolderName,
    dropTargetId,
    dropHint,
    draggedFolderId,
    newFolderInputRef,
    handleToggleFolderExpand,
    handleStartCreateFolder,
    handleCommitNewFolder,
    handleNewFolderKeyDown,
    handleDragOverTarget,
    handleDragLeaveTarget,
    handleFolderDragStart,
    handleFolderDragEnd,
    handleDropOnFolder,
    handleDropOnUnfiled,
    handleCollapseAll,
    canCollapseAll,
  };
};

export default useDocsSection;
